// =============================================================================
// domain/confluenceEditor.ts — editing the trade form's confluence vocabulary.
// -----------------------------------------------------------------------------
// The registry's SHAPE and its parser live in `confluences.ts`, because that is
// where it is read. This is the write half, and it is separate for the reason
// the tag editor's is: only one of the two can destroy something.
//
// ── 🔴 THE SIX DEFAULTS ARE CODE, NOT DATA, AND THE FIRST SAVE ENDS THAT ──
//
// `confluenceRegistry` falls back to `DEFAULT_CONFLUENCES` when the stored value
// is absent, unparseable, or an empty array. Every user who has never edited
// this list is therefore looking at six confluences that exist only in the
// application's source — along with the `hint` text under each one, which
// nothing else supplies.
//
// The moment anything writes a non-empty array, that is over: the fallback can
// never fire again, and if the write dropped the hints they are gone from the
// trade form for good. Two rules come out of that, and both are load-bearing:
//
//   1. NEVER WRITE WHEN NOTHING CHANGED. Every function here returns the SAME
//      array it was given when it would have changed nothing, so a caller can
//      compare by identity and skip the save. Tabbing through a name box must
//      not be what materialises six strangers' entries into a real account.
//   2. WRITE THE HINTS. `registryToStore` carries `hint` through, so the first
//      real edit does not silently blank every tooltip on the trade form.
//
// ── ⚠ AN ID IS PERMANENT, AND A NAME IS NOT ──
//
// Trades store the ids they were ticked with, never the names. So renaming is
// free and instant everywhere — and regenerating an id on rename would orphan
// every trade that carries it. The id is derived from the name ONCE, at
// creation, and never again. Beta does the same and says so in its Add dialog.
// =============================================================================

import type { ConfluenceChild, ConfluenceNode } from './confluences';

/** ⚠ Long enough for "ATAS Footprint Imbalance", short enough to stay a label. */
export const CONFLUENCE_NAME_MAX = 40;

/**
 * `"ATAS Footprint"` → `"atas-footprint"`, uniquified against the WHOLE tree.
 *
 * ⚠ PARENTS AND CHILDREN SHARE ONE ID SPACE, which is beta's rule and not an
 * accident: a trade's store is a flat map of id → active, with no record of
 * which level an id came from. Two nodes sharing an id would make one tick
 * light up two checkboxes.
 */
export function slugifyConfluence(label: string, taken: ReadonlySet<string>): string {
  const base = (label || 'confluence').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, CONFLUENCE_NAME_MAX) || 'confluence';
  let id = base;
  let n = 2;
  while (taken.has(id)) id = `${base}-${n++}`;
  return id;
}

const clean = (s: string): string => s.trim().slice(0, CONFLUENCE_NAME_MAX);

/** Add a top-level confluence. Returns the same array when the name is empty. */
export function addConfluence(
  registry: readonly ConfluenceNode[], label: string, taken: ReadonlySet<string>,
): readonly ConfluenceNode[] {
  const name = clean(label);
  if (!name) return registry;
  return [...registry, {
    id: slugifyConfluence(name, taken), label: name, children: [],
  }];
}

/**
 * Rename one node — a parent or a child, found by id anywhere in the tree.
 *
 * ⚠ AN EMPTY NAME IS REFUSED, not saved. The box is a live input on a row that
 * already exists, so a user who selects-all and pauses has an empty box for a
 * moment; committing it would leave a checkbox on the trade form labelled by its
 * own id. Beta refuses the same way.
 */
export function renameConfluence(
  registry: readonly ConfluenceNode[], id: string, label: string,
): readonly ConfluenceNode[] {
  const name = clean(label);
  if (!name) return registry;
  let hit = false;
  const next = registry.map((c) => {
    if (c.id === id) {
      if (c.label === name) return c;
      hit = true;
      return { ...c, label: name };
    }
    const ci = c.children.findIndex((ch) => ch.id === id);
    if (ci < 0) return c;
    if (c.children[ci]?.label === name) return c;
    hit = true;
    const children = c.children.map((ch) => (ch.id === id ? { ...ch, label: name } : ch));
    return { ...c, children };
  });
  return hit ? next : registry;
}

/**
 * Remove a top-level confluence — AND every sub-confluence under it.
 *
 * 🚨 THIS MOVES NUMBERS, AND THE SCREEN HAS TO SAY SO. The trades are not
 * touched (see `removeChild` for why that is the right call and what it costs),
 * so every trade keeps the ids it was ticked with. Those ids then rank in the
 * Confluence Edge leaderboard under their raw code rather than a name, because
 * nothing can look them up any more.
 *
 * ⚠ And a parent with children does NOT rank today — it is a category — so
 * deleting it can make its children the only thing left, or remove the category
 * entirely. Either way a card the trader has already read changes.
 */
export function removeConfluence(
  registry: readonly ConfluenceNode[], id: string,
): readonly ConfluenceNode[] {
  return registry.some((c) => c.id === id) ? registry.filter((c) => c.id !== id) : registry;
}

/**
 * Add a sub-confluence under a parent.
 *
 * 🚨 THE FIRST ONE CHANGES WHAT THE PARENT MEANS. A confluence WITH children is
 * a category and stops being counted in its own right — its children carry the
 * signal, and counting both would count one observation twice. So adding the
 * first child to a well-used confluence drops every trade whose only mark was
 * that parent to zero marks, and the analytics card moves. That is beta's rule,
 * deliberately ported, and the editor warns before it happens rather than
 * letting a settings edit quietly restate the trader's book.
 */
export function addChild(
  registry: readonly ConfluenceNode[], parentId: string, label: string,
  taken: ReadonlySet<string>,
): readonly ConfluenceNode[] {
  const name = clean(label);
  if (!name) return registry;
  const parent = registry.find((c) => c.id === parentId);
  if (!parent) return registry;
  const child: ConfluenceChild = { id: slugifyConfluence(name, taken), label: name };
  return registry.map((c) => (c.id === parentId ? { ...c, children: [...c.children, child] } : c));
}

/**
 * Remove one sub-confluence.
 *
 * 🚨 THIS IS NOT THE UNDO OF ADDING IT, and that is worth stating because it
 * looks like one. The child's id stays on every trade that carried it and keeps
 * ranking under its raw code; meanwhile the parent, if this was its last child,
 * stops being a category and starts ranking again. One tick can end up counted
 * in two places.
 *
 * ⚠ Rewriting the trades to clean that up is the alternative, and it is worse:
 * hundreds of read-modify-writes on rows a settings screen does not own. Same
 * decision as the custom-tag editor, same reasoning, and the screen says it in
 * words instead of pretending it did not happen.
 */
export function removeChild(
  registry: readonly ConfluenceNode[], parentId: string, childId: string,
): readonly ConfluenceNode[] {
  const parent = registry.find((c) => c.id === parentId);
  if (!parent || !parent.children.some((ch) => ch.id === childId)) return registry;
  return registry.map((c) => (
    c.id === parentId ? { ...c, children: c.children.filter((ch) => ch.id !== childId) } : c
  ));
}

/**
 * The shape `preferences.confluences` stores.
 *
 * 🔴 `hint` IS CARRIED, and this is the line that keeps the trade form's
 * tooltips alive. The six built-in confluences exist only in code, hints
 * included; the first save turns them into stored data forever. A `toStore` that
 * wrote id, label and children — the three fields this editor lets you change —
 * would look complete and would blank every explanation under every checkbox,
 * permanently, in both clients.
 */
export function registryToStore(
  registry: readonly ConfluenceNode[],
): { id: string; label: string; hint: string; children: { id: string; label: string }[] }[] {
  return registry.map((c) => ({
    id: c.id,
    label: c.label,
    // ⚠ Empty string, never undefined — the parser reads a missing hint the
    // same as an empty one, and an undefined is dropped by JSON entirely.
    hint: c.hint ?? '',
    children: c.children.map((ch) => ({ id: ch.id, label: ch.label })),
  }));
}
